import {
  ChartAdapter,
  ChartBuildContext,
  ComboChartData,
  ComboChartSettings,
  ComboSeriesConfig,
  EChartsOption,
  MarkLineConfig,
} from "../types";

import {
  buildDataZoom,
  buildLegend,
  buildTooltip,
  mergeCategorySettings,
} from "./shared";

const GRID_SIDE_PADDING = 40;
const AXIS_OFFSET_STEP = 50;
const DEFAULT_COLORS = [
  "#5470C6",
  "#91CC75",
  "#EE6666",
  "#FAC858",
  "#73C0DE",
  "#3BA272",
  "#FC8452",
  "#9A60B4",
  "#EA7CCC",
];

function hexToRgba(hex: string, alpha: number): string | undefined {
  if (!hex.startsWith("#")) {
    return undefined;
  }
  const normalized =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
  const bigint = parseInt(normalized.slice(1), 16);
  if (Number.isNaN(bigint)) {
    return undefined;
  }
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const DEFAULT_AXIS_ID = "left";

function ensureSeriesValues(
  categories: (string | number)[],
  data: (number | null)[] | undefined
) {
  if (!data) {
    return categories.map(() => null);
  }

  if (data.length >= categories.length) {
    return data.slice(0, categories.length);
  }

  return [...data, ...Array(categories.length - data.length).fill(null)];
}

// 使用一个很小的非零值作为占位符，确保 ECharts 能计算 layout
// 使用 0.01 而不是 0，避免被 ECharts 判断为无效数据
const CANDLESTICK_PLACEHOLDER: [number, number, number, number] = [0.01, 0.01, 0.01, 0.01];

/**
 * 处理 K 线图数据格式
 * K 线图需要 [开盘, 收盘, 最低, 最高] 格式
 * 若缺数据则用占位 [0,0,0,0]，避免 ECharts progress 回调中 getItemLayout(i).sign 报错
 */
function ensureCandlestickValues(
  categories: (string | number)[],
  data: (number | null)[] | number[][] | undefined
): number[][] {
  if (!data) {
    return categories.map(() => [...CANDLESTICK_PLACEHOLDER]);
  }

  if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
    const klineData = data as (number[] | null)[];
    const out: number[][] = [];
    for (let i = 0; i < categories.length; i++) {
      const row = klineData[i];
      if (Array.isArray(row) && row.length >= 4) {
        const open = Number(row[0]);
        const close = Number(row[1]);
        const low = Number(row[2]);
        const high = Number(row[3]);
        // 确保所有值都是有效的有限数字
        // 对于 K 线图，基本要求：最低价 <= 最高价
        // 放宽验证条件，只要数据是有效的数字且 low <= high 就接受
        if (
          Number.isFinite(open) &&
          Number.isFinite(close) &&
          Number.isFinite(low) &&
          Number.isFinite(high)
        ) {
          // 确保 low <= high，如果不符合则交换
          const finalLow = Math.min(low, high);
          const finalHigh = Math.max(low, high);
          // 直接使用原始数据，不进行调整
          // ECharts 可以处理不符合传统K线图逻辑的数据
          out.push([open, close, finalLow, finalHigh]);
        } else {
          // 如果数据无效，使用占位符
          console.warn('[CompsChart] K线图数据无效，使用占位符:', { open, close, low, high, row });
          out.push([...CANDLESTICK_PLACEHOLDER]);
        }
      } else {
        out.push([...CANDLESTICK_PLACEHOLDER]);
      }
    }
    return out;
  }

  if (Array.isArray(data) && typeof data[0] === "number") {
    const flatData = data as (number | null)[];
    const out: number[][] = [];
    for (let i = 0; i < categories.length; i++) {
      const base = i * 4;
      if (base + 3 < flatData.length) {
        const open = Number(flatData[base] ?? 0);
        const close = Number(flatData[base + 1] ?? 0);
        const low = Number(flatData[base + 2] ?? 0);
        const high = Number(flatData[base + 3] ?? 0);
        // 确保所有值都是有效的有限数字
        if (Number.isFinite(open) && Number.isFinite(close) && Number.isFinite(low) && Number.isFinite(high)) {
          out.push([open, close, low, high]);
        } else {
          out.push([...CANDLESTICK_PLACEHOLDER]);
        }
      } else {
        out.push([...CANDLESTICK_PLACEHOLDER]);
      }
    }
    return out;
  }

  return categories.map(() => [...CANDLESTICK_PLACEHOLDER]);
}

function resolveAxisConfigs(data: ComboChartData) {
  const axisConfigs = data.axes || {
    [DEFAULT_AXIS_ID]: {
      id: DEFAULT_AXIS_ID,
      position: "left" as const,
    },
  };

  // axes.bottom / axes.top 仅用于 combo 的类目轴配置（inverse、axisLabel 等），不是数值 Y 轴。
  // 若用 Object.values 一并解析，会把 bottom 当成第三条 value 轴（无 position 时落到 right + offset），
  // 在绘图区最右侧多画一根竖线（订单流速「右侧红线」根因）。
  const axisEntries = Object.entries(axisConfigs)
    .filter(([key]) => key !== "bottom" && key !== "top")
    .map(([, axis]) => axis);

  // 调试：检查轴配置
  if (process.env.NODE_ENV === 'development') {
    console.log('[CompsChart] resolveAxisConfigs 输入:', {
      axes: data.axes,
      axisConfigs,
      axisEntriesCount: axisEntries.length,
      axisEntries: axisEntries.map((a: any) => ({ id: a.id, position: a.position, name: a.name })),
    });
  }
  const axisIndexMap = new Map<string, number>();
  const sideOffsetMax: Record<"left" | "right", number> = {
    left: 0,
    right: 0,
  };
  const positionCounters: Record<string, number> = {};

  const yAxis = axisEntries.map((axis, index) => {
    axisIndexMap.set(axis.id, index);

    const position =
      axis.position ?? (index === 0 ? ("left" as const) : ("right" as const));
    const counter = positionCounters[position] ?? 0;
    positionCounters[position] = counter + 1;
    const offsetValue =
      position === "left" || position === "right"
        ? counter * AXIS_OFFSET_STEP
        : 0;

    if (
      (position === "left" || position === "right") &&
      offsetValue > sideOffsetMax[position]
    ) {
      sideOffsetMax[position] = offsetValue;
    }

    // 透传业务侧 axis.axisLine（含 show / lineStyle），仅认 showAxisLine 会丢掉 OrderTraffic 等配置
    const fromAxisLine =
      (axis as any).axisLine && typeof (axis as any).axisLine === "object"
        ? { ...(axis as any).axisLine }
        : {};
    const axisLine: Record<string, any> = { ...fromAxisLine };
    if (axis.showAxisLine !== undefined) {
      axisLine.show = axis.showAxisLine;
    }

    const splitLine: Record<string, any> = {
      show: index === 0,
    };

    return {
      type: "value",
      id: axis.id,
      position,
      name: axis.name,
      // 如果未设置 min，右侧轴默认从0开始，左侧轴使用 "dataMin" 以自动根据数据调整起始坐标
      min: axis.min !== undefined
        ? axis.min
        : (position === "right" ? 0 : "dataMin"),
      // 如果未设置 max，默认使用 "dataMax" 以自动根据数据调整结束坐标
      max: axis.max !== undefined ? axis.max : "dataMax",
      splitNumber: axis.splitNumber,
      axisLabel:
        axis.axisLabel && axis.axisLabel.formatter
          ? { formatter: axis.axisLabel.formatter }
          : undefined,
      axisLine: Object.keys(axisLine).length > 0 ? axisLine : undefined,
      splitLine: Object.keys(splitLine).length > 0 ? splitLine : undefined,
      offset: offsetValue > 0 ? offsetValue : undefined,
    };
  });

  // 调试：检查轴解析结果
  if (process.env.NODE_ENV === 'development') {
    console.log('[CompsChart] resolveAxisConfigs 输出:', {
      yAxisCount: yAxis.length,
      yAxis: yAxis.map((a: any) => ({ id: a.id, position: a.position, name: a.name, min: a.min, max: a.max })),
      axisIndexMap: Array.from(axisIndexMap.entries()),
    });
  }

  return { yAxis, axisIndexMap, sideOffsetMax };
}

function attachAxisBoundsBySeries(
  yAxis: any[],
  seriesConfig: ComboSeriesConfig[],
  dataset: ComboChartData["dataset"],
  categories: ComboChartData["categories"]
) {
  const axisValueMap = new Map<string, number[]>();

  seriesConfig.forEach((series) => {
    const axisId = series.axisId ?? DEFAULT_AXIS_ID;
    const data = dataset[series.dataKey];

    let values: number[];
    if (series.type === "candlestick" && Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
      // K 线图：从二维数组中提取收盘价（索引 1）
      values = (data as number[][])
        .map((candle) => (Array.isArray(candle) && typeof candle[1] === "number" ? candle[1] : null))
        .filter((value): value is number => typeof value === "number");
    } else {
      // 普通图表：使用一维数组
      values = ensureSeriesValues(categories, data as (number | null)[] | undefined)
        .filter((value): value is number => typeof value === "number")
        .map((value) => value);
    }

    if (!axisValueMap.has(axisId)) {
      axisValueMap.set(axisId, []);
    }

    axisValueMap.get(axisId)?.push(...values);
  });

  return yAxis.map((axis) => {
    // 如果已经设置了 min 或 max，或者没有数据，直接返回
    if ((axis.min !== undefined && axis.min !== null && axis.min !== "dataMin") ||
      (axis.max !== undefined && axis.max !== null && axis.max !== "dataMax") ||
      !axisValueMap.has(axis.id)) {
      return axis;
    }

    const values = axisValueMap.get(axis.id) ?? [];
    if (!values.length) {
      return axis;
    }

    // 只有当 min/max 未设置或为 "dataMin"/"dataMax" 时才自动计算
    // 这里保持使用 "dataMin"/"dataMax"，让 ECharts 自动处理，避免下方空白
    return {
      ...axis,
      min: (axis.min === undefined || axis.min === null || axis.min === "dataMin") ? "dataMin" : axis.min,
      max: (axis.max === undefined || axis.max === null || axis.max === "dataMax") ? "dataMax" : axis.max,
    };
  });
}

type ComboLayout = "vertical" | "horizontal";

function buildSeries(
  ctx: ChartBuildContext<"combo">,
  mergedSettings: Partial<ComboChartSettings>,
  axisIndexMap: Map<string, number>,
  layout: ComboLayout
) {
  const { data } = ctx;
  const isHorizontal = layout === "horizontal";

  // 确保 series 存在，如果不存在则返回空数组
  if (!data.series || !Array.isArray(data.series)) {
    return [];
  }

  // 调试：检查输入的series数据
  if (process.env.NODE_ENV === 'development') {
    const klineSeriesInInput = data.series.find((s: any) => s.type === 'candlestick');
    console.log('[CompsChart] buildSeries 输入检查:', {
      totalSeries: data.series.length,
      hasKlineSeries: !!klineSeriesInInput,
      klineSeriesInfo: klineSeriesInInput ? {
        id: klineSeriesInInput.id,
        type: klineSeriesInInput.type,
        dataKey: klineSeriesInInput.dataKey,
        barWidth: klineSeriesInInput.barWidth,
      } : null,
      allSeriesTypes: data.series.map((s: any) => ({ id: s.id, type: s.type, dataKey: s.dataKey })),
    });
  }

  const comboSeries = data.series.map((series, seriesIndex) => {
    const axisId = series.axisId ?? DEFAULT_AXIS_ID;
    const valueAxisIndex = axisIndexMap.get(axisId) ?? 0;
    const type = series.type;
    const resolvedColor =
      series.color ?? DEFAULT_COLORS[seriesIndex % DEFAULT_COLORS.length];

    // K 线图需要特殊的数据格式处理
    let values: any;
    if (type === "candlestick") {
      const rawData = data.dataset[series.dataKey] as number[][] | undefined;
      values = ensureCandlestickValues(data.categories, rawData);
      // 确保数据长度与 categories 完全匹配，避免维度映射错误
      if (values.length !== data.categories.length) {
        const padded = [...values];
        while (padded.length < data.categories.length) {
          padded.push([...CANDLESTICK_PLACEHOLDER]);
        }
        values = padded.slice(0, data.categories.length);
      }
      // 调试：检查K线图数据
      if (process.env.NODE_ENV === 'development') {
        const hasValidData = values.some((v: number[]) =>
          v && v.length === 4 && v.some((n: number) => n > 0.1) // 降低阈值，只要大于0.1就认为有效
        );
        const placeholderCount = values.filter((v: number[]) =>
          v && v.length === 4 && v[0] === 0.01 && v[1] === 0.01 && v[2] === 0.01 && v[3] === 0.01
        ).length;
        const validDataCount = values.filter((v: number[]) =>
          v && v.length === 4 && v.some((n: number) => n > 0.1)
        ).length;

        console.log('[CompsChart] K线图数据处理结果:', {
          dataKey: series.dataKey,
          rawDataLength: rawData?.length,
          categoriesLength: data.categories.length,
          processedValuesLength: values.length,
          hasValidData,
          validDataCount,
          placeholderCount,
          firstRawData: rawData?.[0],
          firstProcessedValue: values[0],
          sampleProcessedValues: values.slice(0, 3), // 只显示前3个，避免日志过长
          // 检查开盘价和收盘价的差异
          openCloseDiff: values.map((v: number[]) => {
            if (v && v.length >= 2) {
              return Math.abs(v[1] - v[0]); // 收盘价 - 开盘价的绝对值
            }
            return 0;
          }).slice(0, 3),
          // 检查最低价和最高价的差异
          lowHighDiff: values.map((v: number[]) => {
            if (v && v.length >= 4) {
              return v[3] - v[2]; // 最高价 - 最低价
            }
            return 0;
          }).slice(0, 3),
        });

        if (!hasValidData && rawData && rawData.length > 0) {
          console.warn('[CompsChart] ⚠️ K线图数据可能无效，所有数据都被替换为占位符', {
            rawDataSample: rawData.slice(0, 3),
            processedValuesSample: values.slice(0, 3),
          });
        }
        if (placeholderCount > 0) {
          console.warn('[CompsChart] ⚠️ K线图有', placeholderCount, '个数据点被替换为占位符 [0.01, 0.01, 0.01, 0.01]');
        }
        if (validDataCount > 0) {
          console.log('[CompsChart] ✅ K线图有', validDataCount, '个有效数据点');
        }
      }
    } else {
      const rawSeriesData = data.dataset[series.dataKey] as (number | null)[] | undefined;
      values = ensureSeriesValues(data.categories, rawSeriesData);

      // 调试：检查堆叠柱状图数据
      if (process.env.NODE_ENV === 'development' && type === "bar" && series.stack) {
        console.log(`[CompsChart] 堆叠柱状图系列: ${series.name} (${series.dataKey})`, {
          id: series.id,
          dataKey: series.dataKey,
          stack: series.stack,
          rawData: rawSeriesData,
          processedValues: values,
          categoriesLength: data.categories.length,
          valuesLength: values.length,
        });
      }
    }

    const baseSeries: any = {
      id: series.id,
      name: series.name,
      type,
      data: values,
      // 对于 candlestick 在横向布局时，交换 X 轴和 Y 轴的索引
      // 让 ECharts 认为类目在 X 轴，值在 Y 轴，从而绕过 candlestickLayout.js 的硬编码限制
      ...(isHorizontal && type === "candlestick"
        ? { xAxisIndex: 0, yAxisIndex: 0 } // 使用交换后的坐标轴配置
        : isHorizontal
          ? { xAxisIndex: 0, yAxisIndex: 0 }
          : { yAxisIndex: valueAxisIndex }),
      axisId: axisId, // 保留 axisId，用于标记线匹配
      emphasis: { focus: "series" },
    };

    // 调试：检查K线图系列配置
    if (process.env.NODE_ENV === 'development' && type === "candlestick") {
      const firstData = baseSeries.data?.[0];
      const openCloseDiff = firstData && Array.isArray(firstData) && firstData.length >= 2
        ? Math.abs(firstData[1] - firstData[0])
        : 0;
      const lowHighDiff = firstData && Array.isArray(firstData) && firstData.length >= 4
        ? firstData[3] - firstData[2]
        : 0;

      console.log('[CompsChart] K线图系列配置:', {
        id: baseSeries.id,
        name: baseSeries.name,
        type: baseSeries.type,
        yAxisIndex: baseSeries.yAxisIndex,
        axisId: axisId,
        valueAxisIndex,
        dataLength: baseSeries.data?.length,
        firstDataPoint: baseSeries.data?.[0],
        openCloseDiff: openCloseDiff, // 开盘价和收盘价的差异
        lowHighDiff: lowHighDiff, // 最低价和最高价的差异
        isHorizontal,
        // 检查数据格式
        dataFormatValid: baseSeries.data?.every((d: any) =>
          Array.isArray(d) && d.length >= 4 &&
          typeof d[0] === 'number' && typeof d[1] === 'number' &&
          typeof d[2] === 'number' && typeof d[3] === 'number'
        ),
      });
    }

    // 根据系列类型添加特定配置
    if (type === "candlestick") {
      // 注意：ECharts candlestick 不支持横向布局（类目在 Y 轴）
      // 如果 layout 是 horizontal，说明回退逻辑没有生效，这里再次检查并处理
      // 注意：ECharts的large模式可能不支持barWidth配置，导致K线图矩形主体不显示
      // 如果数据量不大（< 1000），禁用large模式以确保barWidth生效
      const dataLength = baseSeries.data?.length || 0;
      const shouldUseLarge = dataLength >= 1000; // 只有数据量>=1000时才使用large模式

      if (isHorizontal) {
        // 横向布局时，由于我们交换了 X 轴和 Y 轴的配置
        // 现在 X 轴是 category（类目），Y 轴是 value（值）
        // 所以 ECharts 的 layout 应该是 'horizontal'（类目在 X 轴）
        baseSeries.layout = "horizontal"; // ECharts 的 horizontal = 类目在 X 轴
        // 只有在数据量很大时才使用large模式
        if (shouldUseLarge) {
          baseSeries.large = true;
          baseSeries.largeThreshold = 0;
          baseSeries.progressive = 0;
        } else {
          // 数据量不大时，禁用large模式以确保barWidth和itemStyle正确生效
          baseSeries.large = false;
        }
        baseSeries.animation = false;
      } else {
        // 竖立布局时，根据数据量决定是否使用large模式
        if (shouldUseLarge) {
          baseSeries.large = true;
          baseSeries.largeThreshold = 0;
          baseSeries.progressive = 0;
        } else {
          // 数据量不大时，禁用large模式以确保barWidth和itemStyle正确生效
          baseSeries.large = false;
        }
      }
      // K 线图配置
      // 如果用户没有提供 itemStyle，使用默认配置
      // 注意：确保K线图的主体（矩形）能够正确显示
      // ECharts K线图需要完整的itemStyle配置才能正确渲染矩形主体
      const defaultItemStyle = {
        color: "#00da3c", // 上涨颜色（收盘价 > 开盘价）- 主体填充色
        color0: "#ec0000", // 下跌颜色（收盘价 < 开盘价）- 主体填充色
        borderColor: "#333", // 上涨边框颜色
        borderColor0: "#333", // 下跌边框颜色
        borderWidth: 0, // 边框宽度，设置为0去掉外侧边框线
        shadowBlur: 0, // 关闭阴影，避免影响显示
        shadowColor: "transparent", // 阴影颜色透明
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      };

      // 合并用户自定义配置
      baseSeries.itemStyle = {
        ...defaultItemStyle,
        ...series.itemStyle, // 用户自定义配置会覆盖默认值
      };

      // 确保关键属性存在
      // 如果用户没有自定义borderWidth，使用默认值0（无边框）
      if (baseSeries.itemStyle.borderWidth === undefined) {
        baseSeries.itemStyle.borderWidth = 0; // 默认无边框
      }
      if (baseSeries.itemStyle.borderColor === undefined) {
        baseSeries.itemStyle.borderColor = "#333";
      }
      if (baseSeries.itemStyle.borderColor0 === undefined) {
        baseSeries.itemStyle.borderColor0 = "#333";
      }
      // 确保颜色设置正确
      if (baseSeries.itemStyle.color === undefined) {
        baseSeries.itemStyle.color = "#00da3c";
      }
      if (baseSeries.itemStyle.color0 === undefined) {
        baseSeries.itemStyle.color0 = "#ec0000";
      }
      // K 线图的宽度和间距配置，避免超出 Y 轴范围
      // 优先级：settings中的barWidth > series中的barWidth > 默认值
      // 这样可以让全局配置（settings）覆盖单个系列的配置
      let finalBarWidth: number | string;
      if (mergedSettings.barWidth !== undefined) {
        // settings中的barWidth优先级最高，可以覆盖series中的配置
        finalBarWidth = mergedSettings.barWidth;
      } else if (series.barWidth !== undefined) {
        finalBarWidth = series.barWidth;
      } else {
        // 默认宽度为类目宽度的 60%，确保不会超出边界
        finalBarWidth = "60%";
      }

      // 确保barWidth被正确设置
      baseSeries.barWidth = finalBarWidth;

      // ECharts K线图：如果barWidth是数字（像素值），同时设置barMaxWidth确保生效
      // 某些ECharts版本中，单独设置像素值可能不生效，需要配合barMaxWidth
      if (typeof finalBarWidth === 'number') {
        // 如果barWidth是像素值，设置barMaxWidth为barWidth的2倍，确保宽度生效
        baseSeries.barMaxWidth = finalBarWidth * 2;
      } else if (typeof finalBarWidth === 'string' && finalBarWidth.endsWith('%')) {
        // 如果是百分比，也设置一个合理的barMaxWidth（例如50像素）
        baseSeries.barMaxWidth = 50;
      } else {
        // 默认情况
        baseSeries.barMaxWidth = 50;
      }

      // 调试：检查K线图宽度配置
      if (process.env.NODE_ENV === 'development') {
        // 检查第一个数据点的开盘价和收盘价差异
        const firstData = baseSeries.data?.[0];
        const openCloseDiff = firstData && Array.isArray(firstData) && firstData.length >= 2
          ? Math.abs(firstData[1] - firstData[0])
          : 0;

        console.log('[CompsChart] K线图宽度配置:', {
          seriesId: series.id,
          seriesName: series.name,
          seriesBarWidth: series.barWidth,
          settingsBarWidth: mergedSettings.barWidth,
          finalBarWidth: baseSeries.barWidth,
          barMaxWidth: baseSeries.barMaxWidth,
          barWidthType: typeof baseSeries.barWidth,
          openCloseDiff: openCloseDiff, // 开盘价和收盘价的差异，影响矩形主体高度
          dataLength: baseSeries.data?.length,
          large: baseSeries.large, // 重要：检查是否使用large模式
          baseSeriesConfig: {
            type: baseSeries.type,
            barWidth: baseSeries.barWidth,
            barMaxWidth: baseSeries.barMaxWidth,
            barCategoryGap: baseSeries.barCategoryGap,
            large: baseSeries.large,
            itemStyle: {
              color: baseSeries.itemStyle?.color,
              color0: baseSeries.itemStyle?.color0,
              borderColor: baseSeries.itemStyle?.borderColor,
              borderColor0: baseSeries.itemStyle?.borderColor0,
              borderWidth: baseSeries.itemStyle?.borderWidth,
            },
          },
        });

        // 如果开盘价和收盘价差异太小，给出警告
        if (openCloseDiff < 5) {
          console.warn('[CompsChart] ⚠️ K线图开盘价和收盘价差异太小（' + openCloseDiff + '），矩形主体可能不明显。建议差异至少为10-20。');
        }
      }

      // 类目间的间距，默认 20%，确保左右两侧有足够空间
      baseSeries.barCategoryGap = series.barCategoryGap ?? mergedSettings.barCategoryGap ?? "20%";
      // 同一类目内的间距（如果有多个 K 线系列）
      if (series.barGap !== undefined) {
        baseSeries.barGap = series.barGap;
      } else if (mergedSettings.barGap !== undefined) {
        baseSeries.barGap = mergedSettings.barGap;
      }
    } else if (type === "line") {
      // 折线图：合并业务侧 lineStyle（虚线/颜色）；订单流速等可用常值 line 代替 markLine，规避 inverse 双轴竖线
      const ext = series as ComboSeriesConfig & {
        lineStyle?: Record<string, unknown>;
        z?: number;
        silent?: boolean;
        showInLegend?: boolean;
        legendHoverLink?: boolean;
      };
      const fromStyle = ext.lineStyle && typeof ext.lineStyle === "object" ? ext.lineStyle : {};
      baseSeries.smooth =
        series.smooth !== undefined
          ? series.smooth
          : mergedSettings.smoothDefault;
      baseSeries.areaStyle =
        series.area ?? mergedSettings.areaDefault
          ? {}
          : undefined;
      baseSeries.showSymbol =
        series.showSymbol !== undefined
          ? series.showSymbol
          : mergedSettings.showSymbolDefault;
      baseSeries.lineStyle = { width: 2, ...fromStyle };
      if (!baseSeries.lineStyle.color) {
        baseSeries.lineStyle.color = resolvedColor;
      }
      if (ext.color) {
        baseSeries.lineStyle.color = ext.color;
      }
      baseSeries.color = baseSeries.lineStyle.color;
      if (ext.z !== undefined) baseSeries.z = ext.z;
      if (ext.silent !== undefined) baseSeries.silent = ext.silent;
      if (ext.showInLegend !== undefined) baseSeries.showInLegend = ext.showInLegend;
      if (ext.legendHoverLink !== undefined) baseSeries.legendHoverLink = ext.legendHoverLink;
    } else if (type === "bar") {
      // 柱状图配置
      // 支持 stackId 或 stack 属性，优先使用 stackId（向后兼容）
      baseSeries.stack = (series as any).stackId || series.stack;
      baseSeries.itemStyle = { color: resolvedColor };
      baseSeries.barWidth = series.barWidth;
      baseSeries.color = resolvedColor;
    }

    const showInLegend = (series as { showInLegend?: boolean }).showInLegend;
    if (typeof showInLegend === "boolean") {
      baseSeries.showInLegend = showInLegend;
    }

    return baseSeries;
  });

  // 调试：检查输出的series数据
  if (process.env.NODE_ENV === 'development') {
    const klineSeriesInOutput = comboSeries.find((s: any) => s.type === 'candlestick');
    console.log('[CompsChart] buildSeries 输出检查:', {
      totalSeries: comboSeries.length,
      hasKlineSeries: !!klineSeriesInOutput,
      klineSeriesInfo: klineSeriesInOutput ? {
        id: klineSeriesInOutput.id,
        type: klineSeriesInOutput.type,
        barWidth: klineSeriesInOutput.barWidth,
        barMaxWidth: klineSeriesInOutput.barMaxWidth,
        dataLength: klineSeriesInOutput.data?.length,
      } : null,
      allSeriesTypes: comboSeries.map((s: any) => ({ id: s.id, type: s.type })),
    });
  }

  return comboSeries;
}

function buildMarkLine(
  markLines?: MarkLineConfig[],
  horizontal?: boolean,
  swappedAxis?: boolean,
) {
  if (!markLines || markLines.length === 0) {
    return undefined;
  }

  const markLineData = markLines.map((config) => {
    const lineStyle = config.color
      ? {
          color: config.color,
          type: config.lineStyle?.type ?? "solid",
          width: config.lineStyle?.width ?? 1,
        }
      : {
          type: config.lineStyle?.type ?? "dashed",
          width: config.lineStyle?.width ?? 1,
        };

    const labelCfg = config.label
      ? {
          show: config.label.show ?? true,
          position: config.label.position ?? "insideEndTop",
          formatter: config.label.formatter ?? config.name ?? "",
        }
      : undefined;

    // 纵向类目在 X（含 inverse）：只用 yAxis 常值画水平无限辅助线。coord 双端点在 inverse 双轴场景下
    // 易在绘图区右缘误连成竖向线段（用户反馈「右侧红线」），故不再使用 coord 分支。

    let axisConfig: any;
    if (horizontal && swappedAxis) {
      axisConfig = { yAxis: config.value };
    } else if (horizontal) {
      axisConfig = { xAxis: config.value };
    } else {
      axisConfig = { yAxis: config.value };
    }

    const markLineItem: any = {
      ...axisConfig,
      name: config.name,
      silent: config.silent ?? false,
      lineStyle,
      ...(labelCfg ? { label: labelCfg } : {}),
    };

    return markLineItem;
  });

  // 若任一配置为 silent，则整组 markLine 设为 silent（ECharts 以组为单位）
  const hasSilent = markLines.some((c) => c.silent === true);
  return {
    // 默认箭头会在贴边处连成竖向线段，inverse 类目轴时易被看成「右侧红线」
    symbol: ["none", "none"] as const,
    data: markLineData,
    silent: hasSilent,
    emphasis: { disabled: true },
  };
}

// 根据 axisId 分组标记线配置
function groupMarkLinesByAxisId(
  markLines: MarkLineConfig[],
  axisIndexMap: Map<string, number>
): Map<string, MarkLineConfig[]> {
  const grouped = new Map<string, MarkLineConfig[]>();

  markLines.forEach((config) => {
    // 如果指定了 axisId，使用指定的 axisId
    // 如果没有指定，使用默认的 "left"
    const axisId = config.axisId ?? DEFAULT_AXIS_ID;

    if (!grouped.has(axisId)) {
      grouped.set(axisId, []);
    }
    grouped.get(axisId)!.push(config);
  });

  return grouped;
}

const comboChartAdapter: ChartAdapter<"combo"> = {
  type: "combo",
  buildOption: (ctx) => {
    const mergedSettings: Partial<ComboChartSettings> = {
      ...mergeCategorySettings(ctx.settings),
      markLines: ctx.settings?.markLines,
      layout: ctx.settings?.layout ?? "vertical",
    };
    const { data } = ctx;

    // 确保 data.series 存在且是数组
    if (!data.series || !Array.isArray(data.series)) {
      console.warn('[CompsChart] data.series 不存在或不是数组，返回空配置', { data });
      return {
        grid: { top: 0, left: 0, right: 0, bottom: 0 },
        tooltip: { show: false },
        legend: { show: false },
        xAxis: [],
        yAxis: [],
        series: [],
      };
    }

    const layout = mergedSettings.layout ?? "vertical";
    const isHorizontal = layout === "horizontal";
    const hasCandlestick = data.series.some((s) => s.type === "candlestick");
    const needSwapAxisForCandlestick = isHorizontal && hasCandlestick;
    const { yAxis: initialYAxis, axisIndexMap, sideOffsetMax } = resolveAxisConfigs(data);
    const series = buildSeries(ctx, mergedSettings, axisIndexMap, layout);

    // 确保 series 是数组
    if (!Array.isArray(series)) {
      console.warn('[CompsChart] buildSeries 返回的不是数组:', series);
      return {
        grid: { top: 0, left: 0, right: 0, bottom: 0 },
        tooltip: { show: false },
        legend: { show: false },
        xAxis: [],
        yAxis: [],
        series: [],
      };
    }

    const axisColorByIndex = new Map<number, { color: string; isBar: boolean; isCandlestick: boolean }>();
    (series as any[]).forEach((item) => {
      const color =
        typeof item.color === "string" ? (item.color as string) : undefined;
      if (!color) {
        return;
      }
      const index = isHorizontal ? (item.xAxisIndex ?? 0) : (item.yAxisIndex ?? 0);
      const existing = axisColorByIndex.get(index);
      if (!existing || (item.type === "bar" && !existing.isBar) || (item.type === "candlestick" && !existing.isCandlestick)) {
        axisColorByIndex.set(index, {
          color,
          isBar: item.type === "bar",
          isCandlestick: item.type === "candlestick",
        });
      }
    });

    if (!axisColorByIndex.has(0) && series.length > 0) {
      const first = series[0] as any;
      const fallbackColor =
        typeof first.color === "string"
          ? (first.color as string)
          : DEFAULT_COLORS[0];
      axisColorByIndex.set(0, {
        color: fallbackColor,
        isBar: first.type === "bar",
        isCandlestick: first.type === "candlestick",
      });
    }

    const valueAxes = isHorizontal ? [initialYAxis[0]].filter(Boolean) : initialYAxis;
    let resolvedYAxis = valueAxes;

    if (ctx.settings?.synchronizeAxisBounds && !isHorizontal && data.series) {
      resolvedYAxis = attachAxisBoundsBySeries(
        valueAxes,
        data.series,
        data.dataset,
        data.categories
      );
    }

    // 强制右侧坐标轴从0开始（即使后端设置了其他值）
    resolvedYAxis = resolvedYAxis.map((axis) => {
      if (axis.position === "right") {
        return { ...axis, min: 0 };
      }
      return axis;
    });

    // 右侧轴避免使用红色，以免与「年同比辅助线」等红色 markLine 混淆；红色时改用与平均价一致的浅蓝
    const isRedLike = (hex: string) => {
      if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return false;
      const h = hex.replace(/^#/, "");
      const r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
      const g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
      const b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
      return r >= 200 && g <= 80 && b <= 80;
    };
    const rightAxisFallbackColor = "#73c0de"; // 平均价常用色，与红色辅助线区分

    const decoratedValueAxes = resolvedYAxis.map((axis, index) => {

      // 左侧坐标轴默认使用蓝色
      const leftAxisColor = "#1890ff"; // 蓝色

      const hint =
        axisColorByIndex.get(index) ??
        (index < DEFAULT_COLORS.length
          ? { color: DEFAULT_COLORS[index], isBar: false, isCandlestick: false }
          : undefined);
      // 如果是左侧轴，优先使用蓝色；否则使用从 series 推断的颜色
      let axisColor = axis.position === "left"
        ? leftAxisColor
        : (hint?.color);
      // 右侧轴若为红色系则改用浅蓝，避免与红色辅助线混成「一根红线无刻度」的误解
      if (axis.position === "right" && axisColor && isRedLike(axisColor)) {
        axisColor = rightAxisFallbackColor;
      }
      if (!axisColor) {
        return axis;
      }

      const axisWithColor: any = { ...axis };
      const axisLineShow = axisWithColor.axisLine?.show === false ? false : true;
      const explicitLineColor = axisWithColor.axisLine?.lineStyle?.color;
      const axisLineColor =
        (typeof explicitLineColor === "string" && explicitLineColor.length > 0)
          ? explicitLineColor
          : axisColor;

      // 关闭轴线时不要带 lineStyle.color，部分 ECharts 版本仍会描边；刻度一并关掉
      if (!axisLineShow) {
        axisWithColor.axisLine = { show: false };
        axisWithColor.axisTick = { show: false };
        // 避免残留纵向网格/次分割线被误看成「右侧红线」
        axisWithColor.splitLine = { show: false };
        axisWithColor.minorSplitLine = { show: false };
        const defaultFormatter =
          axisWithColor.position === "right" && !axisWithColor.axisLabel?.formatter
            ? (value: number) => Math.round(value).toString()
            : undefined;
        const labelColor =
          axis.position === "right" ? rightAxisFallbackColor : axisLineColor;
        axisWithColor.axisLabel = {
          ...(axisWithColor.axisLabel || {}),
          show: axisWithColor.axisLabel?.show !== false,
          color: labelColor,
          ...(defaultFormatter ? { formatter: defaultFormatter } : {}),
        };
        axisWithColor.nameTextStyle = {
          ...(axisWithColor.nameTextStyle || {}),
          color: labelColor,
        };
        if (axis.position === "right" && axisWithColor.name !== undefined) {
          axisWithColor.nameGap = axisWithColor.nameGap ?? 12;
          axisWithColor.show = axisWithColor.show !== false;
        }
        if (axisWithColor.splitLine?.show) {
          const rgba = hexToRgba(labelColor, 0.25);
          axisWithColor.splitLine = {
            ...axisWithColor.splitLine,
            lineStyle: {
              ...(axisWithColor.splitLine?.lineStyle || {}),
              color: rgba ?? labelColor,
            },
          };
        }
        return axisWithColor;
      }

      axisWithColor.axisLine = {
        ...(axisWithColor.axisLine || {}),
        show: axisLineShow,
        lineStyle: {
          ...(axisWithColor.axisLine?.lineStyle || {}),
          color: axisLineColor,
        },
      };
      axisWithColor.axisTick = {
        ...(axisWithColor.axisTick || {}),
        show: axisWithColor.axisTick?.show === false ? false : axisLineShow,
        length: 12,
        inside: true,
        lineStyle: { color: axisLineColor, width: 2 },
      };
      // 右侧价格轴默认去掉小数点，显示整数（如果没有自定义 formatter）；并强制显示刻度标签，避免「只有一根线无刻度」
      const defaultFormatter = axisWithColor.position === "right" && !axisWithColor.axisLabel?.formatter
        ? (value: number) => Math.round(value).toString()
        : undefined;

      axisWithColor.axisLabel = {
        ...(axisWithColor.axisLabel || {}),
        show: axisWithColor.axisLabel?.show !== false,
        color: axisLineColor,
        ...(defaultFormatter ? { formatter: defaultFormatter } : {}),
      };
      axisWithColor.nameTextStyle = {
        ...(axisWithColor.nameTextStyle || {}),
        color: axisLineColor,
      };
      // 右侧轴显式显示轴名称（如「平均价」），避免用户疑惑「这根轴给谁用、刻度是什么」
      if (axis.position === "right" && axisWithColor.name !== undefined) {
        axisWithColor.nameGap = axisWithColor.nameGap ?? 12;
        axisWithColor.show = axisWithColor.show !== false;
      }
      if (axisWithColor.splitLine?.show) {
        const rgba = hexToRgba(axisLineColor, 0.25);
        axisWithColor.splitLine = {
          ...axisWithColor.splitLine,
          lineStyle: {
            ...(axisWithColor.splitLine?.lineStyle || {}),
            color: rgba ?? axisLineColor,
          },
        };
      }

      return axisWithColor;
    });

    const hasBarOrCandlestick = series?.some((item) => item.type === "bar" || item.type === "candlestick") ?? false;

    // 根据图例位置计算 grid 偏移量
    const legendConfig = buildLegend(data.legend, mergedSettings.legend);
    const legendPosition = legendConfig.show === false
      ? null
      : (mergedSettings.legend?.position ?? data.legend?.position ?? "top");

    const LEGEND_MARGIN = 16; // 图例离外边距
    const LEGEND_GAP_HORIZONTAL = 24; // 图例在上下时，与绘制区之间的间距
    const LEGEND_GAP_VERTICAL = 16; // 图例在左右时，与绘制区之间的间距
    const LEGEND_HEIGHT = 24; // 水平图例的估算高度
    const LEGEND_WIDTH = 60; // 垂直图例的估算宽度
    const BASE_MARGIN = 16; // 绘制区到另一边的基础边距

    // 计算 grid 偏移量（已删除所有原有内边距）
    // 图例在上：top = 图例外边距(16) + 图例高度(24) + 间距(24) = 64, bottom = 16, left = 16, right = 16
    // 图例在下：top = 16, bottom = 间距(24) + 图例高度(24) + 图例外边距(16) = 64, left = 16, right = 16
    // 图例在左：top = 16, bottom = 16, left = 图例外边距(16) + 图例宽度(60) + 间距(16) = 92, right = 16
    // 图例在右：top = 16, bottom = 16, left = 16, right = 间距(16) + 图例宽度(60) + 图例外边距(16) = 92

    let gridTop: number;
    if (legendPosition === "top") {
      gridTop = LEGEND_MARGIN + LEGEND_HEIGHT + LEGEND_GAP_HORIZONTAL;
    } else if (legendPosition === "bottom") {
      gridTop = BASE_MARGIN;
    } else if (legendPosition === "left" || legendPosition === "right") {
      // 图例在左右时，上下边距为16
      gridTop = BASE_MARGIN;
    } else {
      gridTop = 40;
    }

    let gridBottom: number;
    if (legendPosition === "bottom") {
      gridBottom = LEGEND_GAP_HORIZONTAL + LEGEND_HEIGHT + LEGEND_MARGIN;
    } else if (legendPosition === "top") {
      gridBottom = BASE_MARGIN;
    } else if (legendPosition === "left" || legendPosition === "right") {
      // 图例在左右时，上下边距为16
      gridBottom = BASE_MARGIN;
    } else {
      gridBottom = 40;
    }

    let gridLeft: number;
    let gridRight: number;
    if (legendPosition === "top" || legendPosition === "bottom") {
      // 图例在上或下时，左右边距为16（删除所有原有内边距）
      gridLeft = BASE_MARGIN;
      // 如果有多个右侧Y轴，需要额外空间
      const rightAxisExtraSpace = sideOffsetMax.right > 0 ? sideOffsetMax.right + 40 : 0; // 每个偏移轴需要额外空间
      const originalRight = BASE_MARGIN + rightAxisExtraSpace;
      // 减少2/3，保留1/3
      gridRight = Math.max(Math.round(originalRight / 3), 0);
    } else {
      // 图例在左右时，删除原有内边距，只保留图例相关空间
      gridLeft = (legendPosition === "left" ? LEGEND_MARGIN + LEGEND_WIDTH + LEGEND_GAP_VERTICAL : BASE_MARGIN);
      // 如果有多个右侧Y轴，需要额外空间
      const rightAxisExtraSpace = sideOffsetMax.right > 0 ? sideOffsetMax.right + 40 : 0; // 每个偏移轴需要额外空间
      const baseRight = (legendPosition === "right" ? LEGEND_GAP_VERTICAL + LEGEND_WIDTH + LEGEND_MARGIN : BASE_MARGIN);
      const originalRight = baseRight + rightAxisExtraSpace;
      // 减少2/3，保留1/3
      gridRight = Math.max(Math.round(originalRight / 3), 0);
    }
    // 合并标记线配置（data 中的配置优先级更高）
    const markLines = data.markLines ?? mergedSettings.markLines;

    // 根据 axisId 分组标记线，并为每个 axisId 构建对应的标记线配置
    const markLinesByAxisId = markLines && markLines.length > 0
      ? groupMarkLinesByAxisId(markLines, axisIndexMap)
      : new Map<string, MarkLineConfig[]>();

    // 如果有标记线，将标记线添加到对应的 series 中
    // 根据 axisId 找到对应的 series 并添加标记线
    // 每个 axisId 的标记线只添加到一个 series 上（优先 line 类型，其次是第一个 series）

    // 先找出每个 axisId 应该添加到哪个 series（优先 line 类型）
    const axisToSeriesIndex = new Map<string, number>();
    if (!isHorizontal) {
      markLinesByAxisId.forEach((_, axisId) => {
        const lineSeriesIndex = series.findIndex((s) => {
          const sAxisId = (s as any).axisId ?? DEFAULT_AXIS_ID;
          return sAxisId === axisId && (s as any).type === "line" && !s.markLine;
        });
        if (lineSeriesIndex >= 0) {
          axisToSeriesIndex.set(axisId, lineSeriesIndex);
        } else {
          const firstSeriesIndex = series.findIndex((s) => {
            const sAxisId = (s as any).axisId ?? DEFAULT_AXIS_ID;
            return sAxisId === axisId && !s.markLine;
          });
          if (firstSeriesIndex >= 0) {
            axisToSeriesIndex.set(axisId, firstSeriesIndex);
          }
        }
      });
    }

    const markLineAttachIndexHorizontal =
      isHorizontal && markLines && markLines.length > 0
        ? Math.max(0, series.findIndex((s) => (s as any).type === "line"))
        : -1;

    // 调试：检查横向图表的 markLine 附加逻辑
    if (process.env.NODE_ENV === 'development' && isHorizontal && markLines && markLines.length > 0) {
      console.log('[CompsChart] 横向图表 markLine 附加检查:', {
        markLinesCount: markLines.length,
        markLines: markLines.map((ml: any) => ({
          value: ml.value,
          name: ml.name,
          axisId: ml.axisId,
        })),
        markLineAttachIndexHorizontal,
        allLineSeries: series
          .map((s, idx) => ({ index: idx, id: (s as any).id, type: (s as any).type, axisId: (s as any).axisId }))
          .filter((s) => s.type === "line"),
      });
    }

    // 调试：检查series在finalSeries生成前的状态
    if (process.env.NODE_ENV === 'development') {
      const klineSeriesBeforeFinal = series.find((s: any) => s.type === 'candlestick');
      console.log('[CompsChart] finalSeries生成前检查:', {
        totalSeries: series.length,
        hasKlineSeries: !!klineSeriesBeforeFinal,
        klineSeriesInfo: klineSeriesBeforeFinal ? {
          id: klineSeriesBeforeFinal.id,
          type: klineSeriesBeforeFinal.type,
          barWidth: klineSeriesBeforeFinal.barWidth,
        } : null,
      });
    }

    const finalSeries = series.map((item, index) => {
      if (item.markLine) {
        return item;
      }
      const seriesAxisId = (item as any).axisId ?? DEFAULT_AXIS_ID;

      if (markLineAttachIndexHorizontal >= 0 && index === markLineAttachIndexHorizontal) {
        const ml = buildMarkLine(
          markLines!,
          isHorizontal,
          needSwapAxisForCandlestick,
        );
        if (ml) return { ...item, markLine: ml };
        return item;
      }

      if (axisToSeriesIndex.get(seriesAxisId) === index) {
        const axisMarkLines = markLinesByAxisId.get(seriesAxisId);
        if (axisMarkLines) {
          const markLineConfig = buildMarkLine(
            axisMarkLines,
            isHorizontal,
            needSwapAxisForCandlestick,
          );
          if (markLineConfig) {
            return { ...item, markLine: markLineConfig };
          }
        }
      }
      return item;
    });

    // 调试：检查finalSeries生成后的状态
    if (process.env.NODE_ENV === 'development') {
      const klineSeriesAfterFinal = finalSeries.find((s: any) => s.type === 'candlestick');
      console.log('[CompsChart] finalSeries生成后检查:', {
        totalSeries: finalSeries.length,
        hasKlineSeries: !!klineSeriesAfterFinal,
        klineSeriesInfo: klineSeriesAfterFinal ? {
          id: klineSeriesAfterFinal.id,
          type: klineSeriesAfterFinal.type,
          barWidth: klineSeriesAfterFinal.barWidth,
          barMaxWidth: klineSeriesAfterFinal.barMaxWidth,
        } : null,
        allSeriesTypes: finalSeries.map((s: any) => ({ id: s.id, type: s.type })),
      });
    }

    // 构建 tooltip 配置，如果有标记线，需要将 trigger 改为 'item' 以支持 markLine 的 tooltip 和点击事件
    const tooltipConfig = buildTooltip(data.tooltip, mergedSettings.tooltip);
    const hasMarkLines = markLines && markLines.length > 0;

    // 如果有标记线，需要自定义 tooltip formatter 以支持 markLine
    let finalTooltip: any = tooltipConfig;
    if (hasMarkLines && tooltipConfig.show !== false) {
      const originalFormatter = tooltipConfig.formatter;
      finalTooltip = {
        ...tooltipConfig,
        trigger: "item", // 必须使用 'item' 才能支持 markLine 的 tooltip 和点击事件
        formatter: (params: any) => {
          // 如果是 markLine，显示标记线的 tooltip
          if (params.componentType === "markLine") {
            const markLineName = params.data?.name || "标记线";
            const markLineValue = params.data?.value ?? params.data?.yAxis ?? params.data?.xAxis ?? "";
            return `${markLineName}: ${markLineValue}`;
          }
          // 否则使用原始的 formatter 或默认格式
          if (originalFormatter) {
            return originalFormatter(params);
          }
          // 默认格式
          if (Array.isArray(params)) {
            // 多系列 tooltip
            let result = params[0].axisValue + "<br/>";
            params.forEach((item: any) => {
              result += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
            });
            return result;
          } else {
            // 单系列 tooltip
            return `${params.marker} ${params.seriesName}<br/>${params.name}: ${params.value}`;
          }
        },
      };
    }

    let xAxis: any[];
    let yAxis: any[];

    // 对于横向布局且有 candlestick 的情况，交换 X 轴和 Y 轴的配置
    // 让 ECharts 认为类目在 X 轴，值在 Y 轴，从而绕过 candlestickLayout.js 的硬编码限制
    // 注意：这样显示出来的图表会是竖立的，但 candlestick 可以正常工作
    // 注意：needSwapAxisForCandlestick 已在上面定义（第770行），这里不再重复声明

    if (isHorizontal) {
      const valueAxis = decoratedValueAxes[0];
      // 尝试从 data.axes.bottom 获取配置（横向布局时，bottom 轴是价格轴）
      const bottomAxisConfig = data.axes?.bottom;

      // 调试：检查 valueAxis 和 bottomAxisConfig 的 min/max 配置
      if (process.env.NODE_ENV === 'development') {
        console.log('[CompsChart] 横向布局轴配置:', {
          hasValueAxis: !!valueAxis,
          valueAxisMin: valueAxis?.min,
          valueAxisMax: valueAxis?.max,
          hasBottomAxisConfig: !!bottomAxisConfig,
          bottomAxisMin: bottomAxisConfig?.min,
          bottomAxisMax: bottomAxisConfig?.max,
          needSwapAxisForCandlestick,
        });
      }

      // 优先使用 bottom 轴的配置（如果有），否则使用 valueAxis
      const priceAxisConfig = bottomAxisConfig || valueAxis;

      if (needSwapAxisForCandlestick) {
        // 交换：X 轴是 category（类目），Y 轴是 value（值）
        // 这样 candlestickLayout.js 就能正确识别维度了

        // 尝试从 data.axes.left（原始类目轴）获取 axisLabel 配置
        const categoryAxisConfig = data.axes?.left;

        // 构建 axisLabel 配置，确保包含所有必要的属性
        const finalAxisLabel: any = categoryAxisConfig?.axisLabel ? {
          ...categoryAxisConfig.axisLabel,
          // 确保强制显示所有标签
          interval: (categoryAxisConfig.axisLabel as any).interval ?? 0,
        } : {
          // 默认强制显示所有标签
          interval: 0,
        };

        // 调试：检查 axisLabel 配置
        if (process.env.NODE_ENV === 'development') {
          console.log('[CompsChart] 横向布局 + K线图：轴交换配置', {
            hasCategoryAxisConfig: !!categoryAxisConfig,
            categoryAxisConfigType: (categoryAxisConfig as any)?.type,
            categoryAxisConfigData: (categoryAxisConfig as any)?.data,
            categoriesCount: data.categories?.length,
            categories: data.categories?.slice(0, 5), // 只显示前5个
            finalAxisLabel,
            hasFormatter: !!finalAxisLabel?.formatter,
            formatterType: typeof finalAxisLabel?.formatter,
          });
        }

        xAxis = [
          {
            type: "category",
            data: data.categories,
            position: "bottom" as const,
            boundaryGap: hasBarOrCandlestick,
            // 支持从类目轴配置中获取 axisLabel（用于文本换行等）
            axisLabel: finalAxisLabel,
          },
        ];
        yAxis = [
          {
            ...valueAxis,
            type: "value",
            position: "left" as const,
            // 优先使用 priceAxisConfig（bottom 轴）的 min/max，确保坐标轴范围正确应用
            min: (typeof priceAxisConfig?.min === 'number' && Number.isFinite(priceAxisConfig.min))
              ? priceAxisConfig.min
              : (priceAxisConfig?.min !== undefined && priceAxisConfig?.min !== "dataMin" && priceAxisConfig?.min !== null
                ? priceAxisConfig.min
                : (valueAxis?.min !== undefined && valueAxis?.min !== "dataMin" && valueAxis?.min !== null
                  ? valueAxis.min
                  : "dataMin")),
            max: (typeof priceAxisConfig?.max === 'number' && Number.isFinite(priceAxisConfig.max))
              ? priceAxisConfig.max
              : (priceAxisConfig?.max !== undefined && priceAxisConfig?.max !== "dataMax" && priceAxisConfig?.max !== null
                ? priceAxisConfig.max
                : (valueAxis?.max !== undefined && valueAxis?.max !== "dataMax" && valueAxis?.max !== null
                  ? valueAxis.max
                  : "dataMax")),
            // 左侧价格轴强制去掉小数点，显示整数
            axisLabel: {
              ...(priceAxisConfig?.axisLabel || valueAxis?.axisLabel || {}),
              formatter: (value: number) => {
                // 始终格式化为整数，不显示小数
                if (!Number.isFinite(value)) return "0";
                return Math.round(value).toString();
              },
            },
          },
        ];
      } else {
        // 正常横向布局：X 轴是 value（值），Y 轴是 category（类目）
        xAxis = [
          {
            ...valueAxis,
            type: "value",
            position: "bottom" as const,
            // 优先使用 priceAxisConfig（bottom 轴）的 min/max，确保坐标轴范围正确应用
            min: (typeof priceAxisConfig?.min === 'number' && Number.isFinite(priceAxisConfig.min))
              ? priceAxisConfig.min
              : (priceAxisConfig?.min !== undefined && priceAxisConfig?.min !== "dataMin" && priceAxisConfig?.min !== null
                ? priceAxisConfig.min
                : (valueAxis?.min !== undefined && valueAxis?.min !== "dataMin" && valueAxis?.min !== null
                  ? valueAxis.min
                  : "dataMin")),
            max: (typeof priceAxisConfig?.max === 'number' && Number.isFinite(priceAxisConfig.max))
              ? priceAxisConfig.max
              : (priceAxisConfig?.max !== undefined && priceAxisConfig?.max !== "dataMax" && priceAxisConfig?.max !== null
                ? priceAxisConfig.max
                : (valueAxis?.max !== undefined && valueAxis?.max !== "dataMax" && valueAxis?.max !== null
                  ? valueAxis.max
                  : "dataMax")),
            // 底部价格轴默认去掉小数点，显示整数（如果没有自定义 formatter）
            axisLabel: (priceAxisConfig?.axisLabel?.formatter || valueAxis?.axisLabel?.formatter)
              ? (priceAxisConfig?.axisLabel || valueAxis?.axisLabel)
              : {
                ...(priceAxisConfig?.axisLabel || valueAxis?.axisLabel || {}),
                formatter: (value: number) => Math.round(value).toString(),
              },
          },
        ];
        yAxis = [
          {
            type: "category",
            data: data.categories,
            position: "left" as const,
            boundaryGap: hasBarOrCandlestick,
          },
        ];
      }
    } else {
      // 检查是否有bottom轴的axisLabel配置（用于控制X轴标签显示）
      const bottomAxisConfig = data.axes?.bottom as { axisLabel?: any; inverse?: boolean } | undefined;
      const xAxisLabelConfig = bottomAxisConfig?.axisLabel ? {
        ...bottomAxisConfig.axisLabel,
        // 确保强制显示所有标签
        interval: (bottomAxisConfig.axisLabel as any).interval ?? 0,
      } : undefined;
      // inverse: true 时类目轴反转，用于订单流速等「最右侧为当前时间、往左为过去」的时序
      const xAxisInverse = bottomAxisConfig?.inverse === true;

      xAxis = [
        {
          type: "category",
          data: data.categories,
          boundaryGap: hasBarOrCandlestick,
          ...(xAxisInverse ? { inverse: true } : {}),
          ...(xAxisLabelConfig ? { axisLabel: xAxisLabelConfig } : {}),
        },
      ];
      yAxis = decoratedValueAxes;
    }

    const option: EChartsOption = {
      textStyle: {
        fontFamily:
          '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif',
      },
      grid: {
        top: gridTop,
        left: gridLeft,
        right: gridRight,
        bottom: gridBottom,
        containLabel: true,
      },
      tooltip: finalTooltip,
      legend: legendConfig,
      xAxis,
      yAxis,
      series: finalSeries,
    };

    // 调试：检查最终配置
    if (process.env.NODE_ENV === 'development') {
      const klineSeries = (option.series as any[])?.find((s: any) => s.type === 'candlestick');
      const firstKlineData = klineSeries?.data?.[0];
      const openCloseDiff = firstKlineData && Array.isArray(firstKlineData) && firstKlineData.length >= 2
        ? Math.abs(firstKlineData[1] - firstKlineData[0])
        : 0;

      console.log('[CompsChart] 最终ECharts配置检查:', {
        hasKlineSeries: !!klineSeries,
        klineSeriesConfig: klineSeries ? {
          id: klineSeries.id,
          name: klineSeries.name,
          type: klineSeries.type,
          yAxisIndex: klineSeries.yAxisIndex,
          axisId: (klineSeries as any).axisId,
          dataLength: klineSeries.data?.length,
          firstDataPoint: klineSeries.data?.[0],
          openCloseDiff: openCloseDiff, // 开盘价和收盘价差异
          barWidth: klineSeries.barWidth, // 重要：检查barWidth
          barMaxWidth: klineSeries.barMaxWidth, // 重要：检查barMaxWidth
          barCategoryGap: klineSeries.barCategoryGap,
          barGap: klineSeries.barGap,
          itemStyle: {
            color: klineSeries.itemStyle?.color,
            color0: klineSeries.itemStyle?.color0,
            borderColor: klineSeries.itemStyle?.borderColor,
            borderColor0: klineSeries.itemStyle?.borderColor0,
            borderWidth: klineSeries.itemStyle?.borderWidth,
          },
          layout: klineSeries.layout,
          large: klineSeries.large,
          // 完整的系列配置（用于调试）
          fullSeriesConfig: JSON.stringify(klineSeries, null, 2).substring(0, 1000), // 只显示前1000字符
        } : null,
        yAxisCount: yAxis?.length,
        yAxisConfigs: yAxis?.map((axis: any, index: number) => ({
          index,
          position: axis.position,
          type: axis.type,
          id: axis.id,
          min: axis.min,
          max: axis.max,
        })),
        inputAxes: data.axes,
        totalSeriesCount: (option.series as any[])?.length,
        seriesTypes: (option.series as any[])?.map((s: any) => ({
          id: s.id,
          type: s.type,
          yAxisIndex: s.yAxisIndex,
          axisId: (s as any).axisId,
        })),
        xAxisInfo: {
          count: xAxis?.length,
          configs: xAxis?.map((axis: any, index: number) => ({
            index,
            type: axis.type,
            position: axis.position,
            dataCount: Array.isArray(axis.data) ? axis.data.length : 0,
            dataSample: Array.isArray(axis.data) ? axis.data.slice(0, 5) : axis.data,
            hasAxisLabel: !!axis.axisLabel,
            axisLabelInterval: (axis.axisLabel as any)?.interval ?? 0,
            hasFormatter: !!axis.axisLabel?.formatter,
          })),
        },
        yAxisInfo: {
          count: yAxis?.length,
          configs: yAxis?.map((axis: any, index: number) => ({
            index,
            type: axis.type,
            position: axis.position,
            min: axis.min,
            max: axis.max,
          })),
        },
      });
    }

    let dataZoom = buildDataZoom(data.dataZoom, mergedSettings.dataZoom);
    if (dataZoom) {
      if (isHorizontal) {
        dataZoom = dataZoom.map((dz: any) => ({ ...dz, yAxisIndex: 0 }));
      }
      option.dataZoom = dataZoom;
    }

    // 后处理：确保所有 candlestick 系列在横向布局时都正确配置
    // 确保K线图的barWidth配置在最终配置中正确设置
    if (option.series) {
      (option.series as any[]).forEach((series: any) => {
        if (series.type === "candlestick") {
          // 确保barWidth配置存在且正确
          // settings中的barWidth优先级最高，始终覆盖series中的配置
          if (mergedSettings.barWidth !== undefined) {
            series.barWidth = mergedSettings.barWidth;
          } else if (!series.barWidth) {
            // 如果settings和series都没有设置，使用默认值
            series.barWidth = 24; // 默认24像素
          }

          // 确保barMaxWidth也被设置（某些ECharts版本需要）
          // 注意：barMaxWidth必须在所有布局中都设置，不仅仅是横向布局
          if (!series.barMaxWidth) {
            if (typeof series.barWidth === 'number') {
              series.barMaxWidth = series.barWidth * 2;
            } else {
              series.barMaxWidth = 50; // 默认最大宽度50像素
            }
          }

          // 确保itemStyle完整配置（某些ECharts版本需要完整的itemStyle才能正确渲染）
          if (!series.itemStyle) {
            series.itemStyle = {};
          }
          // 确保关键属性存在
          if (!series.itemStyle.color) {
            series.itemStyle.color = "#00da3c";
          }
          if (!series.itemStyle.color0) {
            series.itemStyle.color0 = "#ec0000";
          }
          // 如果用户没有自定义borderWidth，使用默认值0（无边框）
          if (series.itemStyle.borderWidth === undefined) {
            series.itemStyle.borderWidth = 0; // 默认无边框
          }
          if (!series.itemStyle.borderColor) {
            series.itemStyle.borderColor = "#333";
          }
          if (!series.itemStyle.borderColor0) {
            series.itemStyle.borderColor0 = "#333";
          }

          // 横向布局时的特殊处理
          if (isHorizontal) {
            // 横向布局时，根据数据量决定是否使用large模式
            // large模式可能不支持barWidth，导致K线图矩形主体不显示
            const dataLength = series.data?.length || 0;
            const shouldUseLarge = dataLength >= 1000;
            if (series.large === undefined) {
              series.large = shouldUseLarge;
            }
            if (series.large && series.largeThreshold === undefined) {
              series.largeThreshold = 0;
            }
            // 只有在使用large模式时才设置progressive
            if (series.large && series.progressive === undefined) {
              series.progressive = 0;
            }
            if (series.animation === undefined) {
              series.animation = false;
            }
            // 确保 layout 设置正确（buildSeries 中已设置为 "horizontal"）
            if (!series.layout) {
              series.layout = "horizontal"; // ECharts 的 horizontal = 类目在 X 轴
            }
          }

          // 强制确保K线图的关键配置存在（某些ECharts版本需要）
          // 确保data是有效的二维数组
          if (!Array.isArray(series.data) || series.data.length === 0) {
            console.warn('[CompsChart] ⚠️ K线图数据无效:', {
              id: series.id,
              dataType: typeof series.data,
              dataIsArray: Array.isArray(series.data),
              dataLength: series.data?.length,
            });
          } else {
            // 验证数据格式
            const firstData = series.data[0];
            if (!Array.isArray(firstData) || firstData.length < 4) {
              console.warn('[CompsChart] ⚠️ K线图数据格式错误:', {
                id: series.id,
                firstData,
                expectedFormat: '[open, close, low, high]',
              });
            }
          }

          // 调试：检查最终K线图配置
          if (process.env.NODE_ENV === 'development') {
            const firstData = series.data?.[0];
            const openCloseDiff = firstData && Array.isArray(firstData) && firstData.length >= 2
              ? Math.abs(firstData[1] - firstData[0])
              : 0;

            console.log('[CompsChart] 最终K线图配置:', {
              id: series.id,
              name: series.name,
              type: series.type,
              barWidth: series.barWidth,
              barMaxWidth: series.barMaxWidth,
              barCategoryGap: series.barCategoryGap,
              barGap: series.barGap,
              itemStyle: {
                color: series.itemStyle?.color,
                color0: series.itemStyle?.color0,
                borderColor: series.itemStyle?.borderColor,
                borderColor0: series.itemStyle?.borderColor0,
                borderWidth: series.itemStyle?.borderWidth,
              },
              layout: series.layout,
              isHorizontal,
              dataLength: series.data?.length,
              firstDataPoint: firstData,
              openCloseDiff: openCloseDiff,
              yAxisIndex: series.yAxisIndex,
              large: series.large, // 重要：检查是否使用large模式
              largeThreshold: series.largeThreshold,
            });

            // 如果开盘价和收盘价差异太小，给出警告
            if (openCloseDiff < 10) {
              console.warn('[CompsChart] ⚠️ K线图开盘价和收盘价差异太小（' + openCloseDiff + '），矩形主体可能不明显。建议差异至少为20-30。');
            }
          }
        }
      });
    }

    // 订单流速：右轴同比/环比参考（常值线或历史 markLine）在双轴 + 类目 inverse 下易误绘贴边竖线
    if (option.series) {
      (option.series as any[]).forEach((s) => {
        const sid = s?.id;
        if (
          sid === "order-traffic-comparison-ref" ||
          (typeof sid === "string" && sid.startsWith("order-traffic-comparison-"))
        ) {
          s.emphasis = { disabled: true };
          s.legendHoverLink = false;
          delete s.markLine;
          delete s.markPoint;
        }
      });
    }

    return option;
  },
  getDefaultSettings: (): Partial<ComboChartSettings> => ({
    legend: { show: true },
    tooltip: { enable: true, trigger: "axis" },
    dataZoom: { enable: false, start: 0, end: 100 },
    areaDefault: false,
    smoothDefault: true,
    synchronizeAxisBounds: false,
    showSymbolDefault: false,
    layout: "vertical",
  }),
};

export default comboChartAdapter;


